import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, InputAdornment, MenuItem, TextField, Typography } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useNavigate } from "react-router-dom";
import { getCoachClientsApi } from "src/DAL/coachWorkspace/coachWorkspace";
import { CW, ErrorBox, Eyebrow, InfoHint, Initials, Loading, PageTitle, Panel, Pill, StatCard, TextLink, UnderlineTabs } from "./cwUi";

/**
 * CLIENTS (3 Nov) — coach "Clients" list (client video 6:03: "if I click
 * into clients … we would have a list of clients"; design PDF sidebar
 * "Clients"). NEW path /coach/clients. Replaces, for coaching, the old
 * Members list (/members), which stays and is linked ("All members").
 * One row per client: journeys, check-in day, last / next check-in, the
 * coach reply still owed. Click → client workspace (/coach/clients/:key).
 */

const COLS = "minmax(0,1.45fr) minmax(0,0.95fr) minmax(0,0.9fr) minmax(0,0.9fr) minmax(0,1fr) 120px";

const TABS = [
  { value: "all", label: "All" },
  { value: "reply", label: "Needs reply" },
  { value: "open", label: "Check-in open" },
  { value: "attention", label: "Needs attention" },
];

const dayOf = (client) => {
  const days = (client.journeys || []).map((j) => String(j.checkInDay || "").split("·")[0].trim()).filter(Boolean);
  if (!days.length) return "—";
  return days.length === 1 ? days[0] : `${days[0]} (+${days.length - 1})`;
};

function ClientRow({ client, onOpen, onReview }) {
  const reply = client.reply;
  const highlight = Boolean(reply?.overdue);
  const journeys = (client.journeys || []).map((j) => j.trackerName).filter(Boolean).join(" · ");
  const own = (client.journeys || []).some((j) => j.ownDay);
  return (
    <Box
      data-testid="client-row"
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: COLS },
        alignItems: "center",
        gap: { xs: 0.5, md: 2 },
        py: { xs: 1.5, md: 1.25 },
        px: { xs: 1.5, md: 2.5 },
        mx: { xs: 0, md: -2.5 },
        my: { xs: 1, md: 0 },
        border: { xs: `1px solid ${CW.line}`, md: 0 },
        borderTop: { md: `1px solid ${CW.line}` },
        borderLeft: { md: highlight ? "3px solid #e8812f" : "3px solid transparent" },
        borderRadius: { xs: "12px", md: 0 },
        bgcolor: highlight ? "#fff7f2" : "#fff",
        cursor: "pointer",
        "&:hover": { bgcolor: highlight ? "#fff1e8" : CW.soft },
      }}
      onClick={onOpen}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, minWidth: 0 }}>
        <Initials name={client.name} size={34} />
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: CW.ink, fontSize: 15 }} noWrap>
            {client.name}
            {client.attention ? (
              <Pill tone="amber" sx={{ ml: 1, fontSize: 10.5, px: 0.9, py: 0.1 }}>
                Follow up
              </Pill>
            ) : null}
          </Typography>
          <Typography sx={{ fontSize: 12, color: CW.muted }} noWrap>
            {journeys || client.email}
          </Typography>
        </Box>
      </Box>
      <Typography sx={{ fontSize: 13.5, color: own ? CW.primary : CW.ink, display: { xs: "none", md: "block" } }} noWrap>
        {dayOf(client)}
      </Typography>
      <Typography sx={{ fontSize: 13, color: CW.muted, display: { xs: "none", md: "block" } }} noWrap>
        {client.lastSubmittedLabel || "—"}
      </Typography>
      <Box sx={{ minWidth: 0, display: { xs: "none", md: "block" } }}>
        {client.open ? (
          <Typography sx={{ fontSize: 12.5, fontWeight: 700, color: CW.amber }} noWrap>
            Open now
          </Typography>
        ) : null}
        <Typography sx={{ fontSize: 13, color: CW.muted }} noWrap>
          {client.next?.label || (client.open ? "" : "—")}
        </Typography>
      </Box>
      <Box sx={{ minWidth: 0 }}>
        {reply ? (
          <>
            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: reply.overdue ? CW.red : CW.ink }} noWrap>
              {reply.deadlineLabel}
            </Typography>
            <Typography sx={{ fontSize: 12, fontWeight: 700, color: reply.state === "feedback_to_send" ? CW.primary : CW.muted }} noWrap>
              {reply.stateLabel}
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: 13, color: CW.green, fontWeight: 600 }}>Up to date</Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }} onClick={(e) => e.stopPropagation()}>
        {reply ? (
          <TextLink onClick={onReview} sx={{ fontSize: 13.5 }}>
            {reply.state === "feedback_to_send" ? "Write feedback" : "Open review"}
          </TextLink>
        ) : (
          <TextLink onClick={onOpen} sx={{ fontSize: 13.5 }}>
            Open client
          </TextLink>
        )}
      </Box>
    </Box>
  );
}

export default function CoachClientsPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [trackerId, setTrackerId] = useState("");
  const [tab, setTab] = useState("all");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    const result = await getCoachClientsApi({ trackerId });
    if (result?.code === 200) setData(result);
    else setError(result?.message || "Could not load your clients");
    setLoading(false);
  }, [trackerId]);

  useEffect(() => {
    load();
  }, [load]);

  const clients = useMemo(() => data?.clients || [], [data]);
  const shown = useMemo(() => {
    const q = search.trim().toLowerCase();
    return clients.filter((c) => {
      if (overdueOnly && !c.reply?.overdue) return false;
      if (tab === "reply" && !c.reply) return false;
      if (tab === "open" && !c.open) return false;
      if (tab === "attention" && !c.attention) return false;
      if (!q) return true;
      return [c.name, c.email, ...(c.journeys || []).map((j) => j.trackerName)]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    });
  }, [clients, tab, overdueOnly, search]);

  if (loading && !data) return <Loading />;
  if (error && !data) return <ErrorBox message={error} onRetry={load} />;

  const totals = data?.totals || {};
  const openClient = (c) => navigate(`/coach/clients/${encodeURIComponent(c.key)}`);
  const openReview = (c) =>
    c.reply?.submissionId
      ? navigate(`/coach/review/${c.reply.submissionId}${c.reply.state === "feedback_to_send" ? "/feedback" : ""}`)
      : openClient(c);
  const pickTab = (value) => {
    setTab(value);
    setOverdueOnly(false);
  };

  return (
    <div className="container-fluid">
      <Box sx={{ maxWidth: 1320, mx: "auto", pb: 4 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: { xs: "flex-start", md: "flex-end" }, justifyContent: "space-between", gap: 2, mb: 3 }}>
          <Box>
            <Eyebrow sx={{ fontSize: 12.5 }}>Coach workspace</Eyebrow>
            <PageTitle>
              Clients
              <InfoHint id="clients.header" />
            </PageTitle>
            <Typography sx={{ fontSize: { xs: 14, md: 17 }, color: CW.muted, mt: 0.5 }}>
              {totals.clients
                ? `${totals.clients} client${totals.clients === 1 ? "" : "s"} on your journeys.`
                : "No clients on your journeys yet."}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", flexDirection: "column", alignItems: { xs: "flex-start", md: "flex-end" }, gap: 1 }}>
            <button type="button" className="small-contained-outlined-button" style={{ minWidth: 160, padding: "10px 18px" }} onClick={() => navigate("/coach/today")}>
              Today
            </button>
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3, position: "relative" }}>
          <InfoHint id="clients.cards" sx={{ position: "absolute", top: -22, right: 0 }} />
          <StatCard
            label="Reply overdue"
            value={totals.overdue || 0}
            hint="Oldest first"
            dot={CW.red}
            valueColor={CW.red}
            active={overdueOnly}
            onClick={() => {
              setTab("all");
              setOverdueOnly((v) => !v);
            }}
          />
          <StatCard label="Needs reply" value={totals.needsReply || 0} hint="Review or feedback" dot={CW.primary} valueColor={CW.primary} active={tab === "reply" && !overdueOnly} onClick={() => pickTab("reply")} />
          <StatCard label="Check-in open" value={totals.open || 0} hint="Not submitted yet" dot="#b06d1a" valueColor="#8a5a14" active={tab === "open"} onClick={() => pickTab("open")} hideOnPhone />
          <StatCard label="Needs attention" value={totals.attention || 0} hint="Two missed in a row" dot={CW.amber} valueColor={CW.amber} active={tab === "attention"} onClick={() => pickTab("attention")} hideOnPhone />
        </Box>

        <Panel title="Your clients" info="clients.list" action={<Pill tone="teal">{shown.length} shown</Pill>}>
          <UnderlineTabs value={tab} onChange={pickTab} options={TABS} sx={{ mb: 2 }} />
          <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap", alignItems: "center", mb: 1.5 }}>
            <TextField
              size="small"
              placeholder="Search client"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              inputProps={{ "aria-label": "Search client" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              sx={{ minWidth: { xs: "100%", sm: 260 }, bgcolor: CW.soft, "& fieldset": { borderColor: "transparent" } }}
            />
            <TextField
              select
              size="small"
              value={trackerId}
              onChange={(e) => setTrackerId(e.target.value)}
              SelectProps={{ displayEmpty: true }}
              inputProps={{ "aria-label": "Programme" }}
              sx={{ minWidth: { xs: "100%", sm: 240 }, bgcolor: CW.soft, "& fieldset": { borderColor: "transparent" } }}
            >
              <MenuItem value="">All</MenuItem>
              {(data?.filters?.trackers || []).map((t) => (
                <MenuItem key={t.id} value={t.id}>
                  {t.name}
                </MenuItem>
              ))}
            </TextField>
            {overdueOnly ? (
              <Pill tone="red" sx={{ cursor: "pointer" }}>
                <Box component="span" onClick={() => setOverdueOnly(false)}>
                  Overdue only ✕
                </Box>
              </Pill>
            ) : null}
          </Box>

          <Box sx={{ display: { xs: "none", md: "grid" }, gridTemplateColumns: COLS, gap: 2, py: 1, px: 2.5, mx: -2.5 }}>
            {["Client", "Check-in day", "Last check-in", "Next check-in", "Coach reply", "Action"].map((h) => (
              <Eyebrow key={h}>{h}</Eyebrow>
            ))}
          </Box>
          {shown.length === 0 ? (
            <Typography sx={{ color: CW.muted, fontSize: 14, py: 3, textAlign: "center", borderTop: `1px solid ${CW.line}` }}>
              {clients.length ? "No clients match." : "No clients yet. Clients appear here once they start one of your journeys."}
            </Typography>
          ) : (
            shown.map((c) => <ClientRow key={c.key} client={c} onOpen={() => openClient(c)} onReview={() => openReview(c)} />)
          )}
          <Typography sx={{ fontSize: 12.5, color: CW.muted, mt: 2 }}>
            Clients on your check-in journeys. Members who never started a journey are on the old Members page.
          </Typography>
        </Panel>
      </Box>
    </div>
  );
}
